import { Delete, Info } from "@mui/icons-material";
import { css, IconButton, Paper, styled } from "@mui/material";
import { ReadonlyVec2, vec2 } from "gl-matrix";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { CanvasHudLineLabel } from "components/canvasHud/lineLabel";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CORE_WINDOW_IDS, useDispatchWindows, useWindow, windowsActions } from "contexts/windows";
import { undoRedoActions } from "features/undoRedo";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { useIsPanelOpenUnified, useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";
import { selectIsProcoreDesign, selectIsProcoreMobileDesign } from "slices/explorer";
import { hasMouseSupport } from "utils/misc";

import { measureInteractionPositionsRef } from "./measureInteractionPositionsRef";
import { measureActions, selectMeasure } from "./measureSlice";
import { getSurfaceKey } from "./utils";

const DimensionMenuButton = styled(IconButton)(
    () => css`
        background-color: #d6dadc;
        width: 24px;
        height: 24px;
        &:hover {
            background-color: #c6cacc;
        }
    `,
);

const DeleteButton = styled(IconButton)(
    () => css`
        background-color: #fff;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        &:hover {
            background-color: #f0f0f0;
        }
    `,
);

function DimensionMenu({
    pos,
    onDelete,
    onInfo,
    hideInfoButton,
    onPointerLeave,
}: {
    pos?: ReadonlyVec2;
    onDelete: () => void;
    onInfo: () => void;
    hideInfoButton?: boolean;
    onPointerLeave?: () => void;
}) {
    if (!pos) {
        return null;
    }

    return (
        <Paper
            onPointerLeave={onPointerLeave}
            style={{
                position: "absolute",
                left: `${pos[0]}px`,
                top: `${pos[1]}px`,
                transform: "translate(-50%, -50%)",
                borderRadius: "100px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                padding: "4px",
            }}
        >
            {!hideInfoButton && (
                <DimensionMenuButton onClick={onInfo}>
                    <Info fontSize="small" />
                </DimensionMenuButton>
            )}
            <DimensionMenuButton onClick={onDelete}>
                <Delete fontSize="small" />
            </DimensionMenuButton>
        </Paper>
    );
}

const MENU_VISIBLITY_SQR_DIST = 40 * 40;

function getFirstAvailableAxisPosition(removeAxisPositions?: Record<string, ReadonlyVec2>) {
    if (!removeAxisPositions) {
        return null;
    }

    return (
        removeAxisPositions.dist ||
        removeAxisPositions.plan ||
        removeAxisPositions.normal ||
        removeAxisPositions.x ||
        removeAxisPositions.y ||
        removeAxisPositions.z ||
        null
    );
}

export const DimensionHtmlInteractions = forwardRef(function DimensionHtmlInteractions(_, ref) {
    const {
        state: { canvas },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const { selectedEntities, duoMeasurementValues, activeAxis, currentIndex } = useAppSelector(selectMeasure);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const dispatchWindows = useDispatchWindows();
    const { openPanel } = useUnifiedPanelToggle();
    const uom = useUom();
    const hasMouse = hasMouseSupport();
    const dimensionWindow = useWindow(CORE_WINDOW_IDS.DIMENSION);
    const isDimensionPanelOpen = useIsPanelOpenUnified(CORE_WINDOW_IDS.DIMENSION);

    const [hoveredSurfaceKey, setHoveredSurfaceKey] = useState<number | null>(null);
    const lastPointerPos = useRef<ReadonlyVec2 | null>(null);
    const [, triggerAreaRedraw] = useState(0); // Triggers area/menu re-render on camera move

    useImperativeHandle(
        ref,
        () => ({
            update() {
                triggerAreaRedraw((prev) => prev + 1);
            },
        }),
        [],
    );

    // On mobile, force a delayed re-render after selectedEntities changes
    // so that measureInteractionPositionsRef has been populated by the canvas update.
    useEffect(() => {
        if (hasMouse) {
            return;
        }
        const timer = setTimeout(() => triggerAreaRedraw((prev) => prev + 1), 200);
        return () => clearTimeout(timer);
    }, [hasMouse, selectedEntities]);

    useEffect(() => {
        if (!canvas || !hasMouse) {
            return;
        }

        function handlePointerMove(event: PointerEvent) {
            const pos = vec2.fromValues(event.offsetX, event.offsetY);
            lastPointerPos.current = pos;

            const { surfaceInfo = {} } = measureInteractionPositionsRef.current;
            const hoveredKey = Object.entries(surfaceInfo).find(
                ([, data]) => data && vec2.sqrDist(data.center, pos) <= MENU_VISIBLITY_SQR_DIST,
            )?.[0];

            setHoveredSurfaceKey(hoveredKey ? Number(hoveredKey) : null);
        }

        canvas.addEventListener("pointermove", handlePointerMove);
        return () => {
            canvas.removeEventListener("pointermove", handlePointerMove);
        };
    }, [hasMouse, canvas]);

    if (!isProcoreDesign) {
        return null;
    }

    const { lengthLabels = [], surfaceInfo = {}, remove, removeAxis } = measureInteractionPositionsRef.current;
    const isDimensionWindowOpen = isProcoreMobileDesign ? isDimensionPanelOpen : dimensionWindow?.state.open;

    const handleDeleteSet = (idx: number) => {
        const setsToRestore = selectedEntities.map((set) => structuredClone(set));
        dispatch(measureActions.deleteMeasureSet(idx));
        dispatch(
            undoRedoActions.push({
                description: "Remove dimension measurement",
                undoCallback: () => {
                    if (setsToRestore.length === 1) {
                        dispatch(measureActions.setSelectedEntities(setsToRestore[0]));
                    } else {
                        setsToRestore.forEach((entities, i) =>
                            dispatch(measureActions.setMeasureSet({ idx: i, entities })),
                        );
                    }
                },
                redoCallback: () => dispatch(measureActions.deleteMeasureSet(idx)),
            }),
        );
    };
    const handleOpenDimensionWindow = (idx: number) => {
        dispatch(measureActions.selectMeasureSet(idx));
        if (isProcoreMobileDesign) {
            openPanel(CORE_WINDOW_IDS.DIMENSION);
        } else {
            dispatchWindows(windowsActions.openWindow(CORE_WINDOW_IDS.DIMENSION, "bottomRight"));
        }
    };

    return (
        <>
            {selectedEntities.flatMap((entities, idx) => {
                if (!entities.length || (!hasMouse && idx !== currentIndex)) {
                    return [];
                }

                return entities.map((entity, entityIdx) => {
                    if (!entity || entity.drawKind !== "face") {
                        return entityIdx === 0 && hasMouse ? (
                            <DimensionMenu
                                key={`measureMenu-${idx}`}
                                pos={remove[idx]}
                                onDelete={() => handleDeleteSet(idx)}
                                onInfo={() => handleOpenDimensionWindow(idx)}
                                hideInfoButton={isDimensionWindowOpen}
                            />
                        ) : null;
                    }

                    const surfaceKey = getSurfaceKey(idx, entityIdx);
                    const surfaceData = surfaceInfo[surfaceKey];
                    if (!surfaceData) {
                        return null;
                    }

                    const isHovered = hoveredSurfaceKey === surfaceKey || !hasMouse;
                    const areaLabel = surfaceData.area
                        ? convertAndFormat(uom, surfaceData.area, UnitType.area, { decimals: 3 })
                        : "No area";

                    return isHovered && hasMouse ? (
                        <DimensionMenu
                            key={`surfaceMenu-${idx}-${entityIdx}`}
                            pos={surfaceData.center}
                            onDelete={() => {
                                const entitiesToRestore = structuredClone(entities);
                                dispatch(measureActions.deleteEntityFromSet({ setIdx: idx, entityIdx }));
                                dispatch(
                                    undoRedoActions.push({
                                        description: "Remove dimension measurement",
                                        undoCallback: () =>
                                            dispatch(measureActions.setSelectedEntities(entitiesToRestore)),
                                        redoCallback: () =>
                                            dispatch(
                                                measureActions.deleteEntityFromSet({
                                                    setIdx: idx,
                                                    entityIdx,
                                                }),
                                            ),
                                    }),
                                );
                            }}
                            onInfo={() => handleOpenDimensionWindow(idx)}
                            hideInfoButton={isDimensionWindowOpen}
                            onPointerLeave={() => setHoveredSurfaceKey(null)}
                        />
                    ) : (
                        <CanvasHudLineLabel
                            key={`surfaceLabel-${idx}-${entityIdx}`}
                            id={`surfaceLabel-${idx}-${entityIdx}`}
                            bgColor="#000000"
                            textColor="#FFFFFF"
                            left={`${surfaceData.center[0]}px`}
                            top={`${surfaceData.center[1]}px`}
                            fontFamily="Open Sans, sans-serif"
                            fontSize="16px"
                            fontWeight={700}
                            lineHeight="20px"
                            onPointerEnter={() => setHoveredSurfaceKey(surfaceKey)}
                            onPointerLeave={() => setHoveredSurfaceKey(null)}
                        >
                            {areaLabel}
                        </CanvasHudLineLabel>
                    );
                });
            })}
            {lengthLabels
                .filter((label) => label.text)
                .map((label) => (
                    <CanvasHudLineLabel
                        key={label.id}
                        id={label.id}
                        bgColor="#000"
                        textColor="#FFF"
                        left={`${label.position2d?.[0] ?? -100}px`}
                        top={`${label.position2d?.[1] ?? -100}px`}
                    >
                        {label.text}
                    </CanvasHudLineLabel>
                ))}
            {hasMouse &&
                duoMeasurementValues.map((result, idx) => {
                    if (!result || !activeAxis[idx]) {
                        return null;
                    }

                    const distPos = getFirstAvailableAxisPosition(removeAxis[idx]);
                    if (!distPos) {
                        return null;
                    }

                    return (
                        <DeleteButton
                            key={`duoMeasureDelete-${idx}`}
                            onClick={() => handleDeleteSet(idx)}
                            style={{
                                position: "absolute",
                                left: `${distPos[0]}px`,
                                top: `${distPos[1]}px`,
                                transform: "translate(-50%, -50%)",
                            }}
                        >
                            <Delete fontSize="small" />
                        </DeleteButton>
                    );
                })}
        </>
    );
});
